import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import clsx from 'clsx';
import type { CalendarRangeResponse, HolidayDto, HolidayScope } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { HOLIDAY_SCOPE_LABEL, HolidayDrawer } from '../components/calendar/HolidayDrawer';
import {
  academyToday,
  addDays,
  formatDay,
  formatMonth,
  formatRange,
  monthEnd,
  monthStart,
  shiftMonth,
  weekdayMondayFirst,
} from '../components/calendar/format';
import type { BatchRecord } from '../components/academics/types';

export const Route = createFileRoute('/_authed/system/calendar')({
  component: AcademyCalendarPage,
});

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SCOPE_TONE: Record<HolidayScope, string> = {
  all: 'bg-[color:var(--color-danger)]/15 text-[color:var(--color-danger)]',
  online: 'bg-sky-500/15 text-sky-800 dark:text-sky-200',
  offline: 'bg-gold-50 text-gold-900 dark:bg-gold-700/25 dark:text-gold-100',
  batch: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text)]',
};

const inRange = (date: string, start: string, end: string) => date >= start && date <= end;

function AcademyCalendarPage() {
  const canEdit = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin'));
  const today = academyToday();
  const [month, setMonth] = useState(() => monthStart(today));
  const [editing, setEditing] = useState<HolidayDto | Record<string, never> | null>(null);
  const [initialDate, setInitialDate] = useState<string | undefined>(undefined);

  // Grid spans whole weeks around the month (Monday-first).
  const gridStart = addDays(month, -weekdayMondayFirst(month));
  const gridEnd = addDays(monthEnd(month), 6 - weekdayMondayFirst(monthEnd(month)));

  const rangeQuery = useQuery({
    queryKey: ['calendar', 'range', gridStart, gridEnd] as const,
    queryFn: ({ signal }) =>
      apiRequest<CalendarRangeResponse>('/api/calendar', { signal, query: { from: gridStart, to: gridEnd } }),
  });
  const upcomingQuery = useQuery({
    queryKey: ['calendar', 'holidays', 'upcoming', today] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: HolidayDto[] }>('/api/calendar/holidays', {
        signal,
        query: { from: today, to: addDays(today, 365) },
      }),
  });
  const batchesQuery = useQuery({
    queryKey: ['batches', 'calendar-pool'] as const,
    enabled: canEdit && editing !== null,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', { signal, query: { page: 1, pageSize: 100 } }),
    staleTime: 60_000,
  });

  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) out.push(d);
    return out;
  }, [gridStart, gridEnd]);

  const data = rangeQuery.data;
  const dayInfo = (date: string) => ({
    holidays: (data?.holidays ?? []).filter((h) => inRange(date, h.startDate, h.endDate)),
    onLeave: (data?.leaves ?? []).filter((l) => l.status === 'approved' && inRange(date, l.startDate, l.endDate)).length,
    cancelled: (data?.sessions ?? []).filter((s) => s.status === 'cancelled' && s.scheduledAt.slice(0, 10) === date).length,
    extra: (data?.sessions ?? []).filter((s) => s.status !== 'cancelled' && s.kind !== 'regular' && s.scheduledAt.slice(0, 10) === date).length,
  });

  const openCreate = (date?: string) => {
    setInitialDate(date);
    setEditing({});
  };

  return (
    <ListShell
      title="Academy calendar"
      description="Holidays and closures. Classes on these days expect no attendance and no teacher reports — and never break a teacher’s report streak."
      primaryAction={
        canEdit ? (
          <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => openCreate()}>
            Add holiday
          </Button>
        ) : undefined
      }
    >
      <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 sm:p-4">
        <header className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-display text-lg font-bold">{formatMonth(month)}</h2>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setMonth(monthStart(today))}>
              Today
            </Button>
            <Button size="sm" variant="secondary" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeft size={16} aria-hidden="true" />
            </Button>
            <Button size="sm" variant="secondary" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
              <ChevronRight size={16} aria-hidden="true" />
            </Button>
          </div>
        </header>
        {rangeQuery.isError ? (
          <ErrorState
            title="Could not load the calendar"
            description={rangeQuery.error instanceof ApiError ? rangeQuery.error.message : undefined}
            onRetry={() => void rangeQuery.refetch()}
          />
        ) : (
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5" role="grid" aria-label={formatMonth(month)}>
            {WEEKDAYS.map((d) => (
              <div key={d} role="columnheader" className="pb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-text-muted)]">
                <span className="sm:hidden">{d[0]}</span>
                <span className="hidden sm:inline">{d}</span>
              </div>
            ))}
            {days.map((date) => {
              const info = dayInfo(date);
              const outside = date.slice(0, 7) !== month.slice(0, 7);
              const isToday = date === today;
              const first = info.holidays[0];
              const label = [
                formatDay(date, { year: false }),
                ...info.holidays.map((h) => `${h.name} (${HOLIDAY_SCOPE_LABEL[h.scope]})`),
                info.onLeave ? `${info.onLeave} teacher(s) on leave` : '',
                info.cancelled ? `${info.cancelled} class(es) cancelled` : '',
                info.extra ? `${info.extra} extra class(es)` : '',
              ]
                .filter(Boolean)
                .join(', ');
              const content = (
                <>
                  <span className={clsx('text-xs font-semibold tabular-nums', isToday && 'text-gold-700 dark:text-gold-300')}>
                    {Number(date.slice(8, 10))}
                  </span>
                  {rangeQuery.isLoading ? (
                    <Skeleton className="mt-1 h-3 w-3/4" />
                  ) : (
                    <span className="mt-0.5 flex min-w-0 flex-col gap-0.5">
                      {info.holidays.slice(0, 2).map((h) => (
                        <span
                          key={h.id}
                          className={clsx('hidden truncate rounded px-1 text-[10px] font-semibold sm:block', SCOPE_TONE[h.scope])}
                        >
                          {h.name}
                        </span>
                      ))}
                      {info.holidays.length > 0 ? (
                        <span className={clsx('h-1.5 w-1.5 rounded-full sm:hidden', first ? SCOPE_TONE[first.scope] : '')} aria-hidden="true" />
                      ) : null}
                      {info.onLeave || info.cancelled || info.extra ? (
                        <span className="hidden truncate text-[10px] text-[color:var(--color-text-muted)] sm:block">
                          {[
                            info.onLeave ? `${info.onLeave} on leave` : '',
                            info.cancelled ? `${info.cancelled} cancelled` : '',
                            info.extra ? `${info.extra} extra` : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      ) : null}
                    </span>
                  )}
                </>
              );
              const cellClass = clsx(
                'flex min-h-[52px] flex-col items-start rounded-md border p-1 text-left sm:min-h-[84px] sm:p-1.5',
                outside ? 'border-transparent opacity-45' : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]',
                isToday && 'ring-2 ring-gold-500',
                info.holidays.length > 0 && !outside && 'bg-[color:var(--color-danger)]/5',
              );
              return canEdit ? (
                <button
                  key={date}
                  type="button"
                  role="gridcell"
                  aria-label={label}
                  className={clsx(cellClass, 'hover:border-gold-500 focus-visible:outline-2 focus-visible:outline-gold-500')}
                  onClick={() => (first ? setEditing(first) : openCreate(date))}
                >
                  {content}
                </button>
              ) : (
                <div key={date} role="gridcell" aria-label={label} className={cellClass}>
                  {content}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[color:var(--color-text-muted)]">
          {(Object.keys(SCOPE_TONE) as HolidayScope[]).map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <span className={clsx('inline-block h-2.5 w-2.5 rounded-sm', SCOPE_TONE[s])} aria-hidden="true" />
              {HOLIDAY_SCOPE_LABEL[s]}
            </span>
          ))}
          <Link to="/academics/leave" className="text-gold-700 hover:underline dark:text-gold-300">
            Teacher leave →
          </Link>
        </p>
      </section>

      <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 sm:p-4">
        <h2 className="mb-2 text-sm font-semibold">Upcoming holidays</h2>
        {upcomingQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : upcomingQuery.isError ? (
          <ErrorState
            title="Could not load the upcoming holidays"
            description={upcomingQuery.error instanceof ApiError ? upcomingQuery.error.message : undefined}
            onRetry={() => void upcomingQuery.refetch()}
          />
        ) : (upcomingQuery.data?.items ?? []).length === 0 ? (
          <EmptyState
            title="No holidays planned"
            description={canEdit ? 'Add Eid, Onam, exam breaks and centre closures so reminders and reports skip those days.' : undefined}
            icon={<CalendarRange size={28} aria-hidden="true" />}
          />
        ) : (
          <ul className="divide-y divide-dashed divide-[color:var(--color-border)]">
            {(upcomingQuery.data?.items ?? []).map((h) => (
              <li key={h.id} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium">{h.name}</p>
                  <p className="text-sm text-[color:var(--color-text-muted)]">
                    {formatRange(h.startDate, h.endDate)} ·{' '}
                    {h.scope === 'batch' ? `Batch ${h.batchName ?? ''}` : HOLIDAY_SCOPE_LABEL[h.scope]}
                    {h.notes ? ` · ${h.notes}` : ''}
                  </p>
                </div>
                {canEdit ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(h)}>
                    Edit
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {canEdit ? (
        <HolidayDrawer
          editing={editing}
          onClose={() => setEditing(null)}
          batches={(batchesQuery.data?.items ?? []).filter((b) => b.status === 'active').map((b) => ({ id: b.id, name: b.name }))}
          initialDate={initialDate}
        />
      ) : null}
    </ListShell>
  );
}
